import { default as isBefore } from "date-fns/isBefore";
import { isDateNotInitialAndInMMDDYYYY, DateStatus } from "../../services/DateUtil";
import { isEqual } from "date-fns";
import { ISLEntryResponse } from "./SLCreateSchemas";
import { ISLEntryGridItem } from "../../schemas/SLEntryGridSchemas";
import { leftPadDocumentNumber } from "../../utilities/utilities";

export const maxHeaderFieldLength = {
  companyCode: 4,
  reference: 16,
  amount: 13,
  taxJurisdictionCode: 2,
  description: 25,
  purpose: 250
};

export const minHeaderFieldLength = {
  reference: 1
};

function requiredLength(inputName: string, inputValue: string): string {
  if (!maxHeaderFieldLength.hasOwnProperty(inputName)) return "";

  const configuredMaxLength = (maxHeaderFieldLength as any)[inputName];
  const keyName = `${inputName[0].toUpperCase()}${inputName.slice(1)}`;
  if (inputName === "reference") {
    const configuredMinLength = (minHeaderFieldLength as any)[inputName];
    if (inputValue.length > configuredMaxLength || inputValue.length < configuredMinLength) {
      return `${keyName} must be between the range - ${configuredMinLength} - ${configuredMaxLength} characters.`;
    }
  } else if (inputValue.length !== configuredMaxLength) {
    return `${keyName} must be ${configuredMaxLength} characters.`;
  }

  if (!inputValue) {
    return `${keyName} is required`;
  }

  return "";
}

function maxLength(inputName: string, inputValue: string): string {
  if (!maxHeaderFieldLength.hasOwnProperty(inputName)) return "";

  const configuredMaxLength = (maxHeaderFieldLength as any)[inputName];

  if (inputValue.length > configuredMaxLength) {
    return `Max length is ${configuredMaxLength}.`;
  }

  if (!inputValue) {
    return `${inputName[0].toUpperCase()}${inputName.slice(1)} is required`;
  }

  return "";
}

function dateMustBeMMDDYYYY(value: string): string {
  const status = isDateNotInitialAndInMMDDYYYY(value);
  if (status === DateStatus.InvalidFormat || status === DateStatus.Initial) {
    return "Invalid format. Format is MM/DD/YYYY";
  }

  return "";
}

function translationDateNotInTheFuture(translationDate: string, postingDate: string): string {
  const errorIfDateIsInTheFuture = (v: string) => {
    if (!isBefore(new Date(v), new Date(postingDate)) && !isEqual(new Date(v), new Date(postingDate))) {
      return "Date can't be in the future unless it is same as the posting date.";
    }
    return "";
  };

  if (translationDate === "") {
    return "";
  } else {
    if ((translationDate as any) instanceof Date) {
      return errorIfDateIsInTheFuture(translationDate);
    }
    const status = isDateNotInitialAndInMMDDYYYY(translationDate);
    if (status === DateStatus.InvalidFormat || status === DateStatus.Initial) {
      return "Invalid format. Format is MM/DD/YYYY";
    }
    if (status === DateStatus.None) {
      return errorIfDateIsInTheFuture(translationDate);
    }
  }

  return "";
}

function validateCodeInCodes(codes: string[], code: string) {
  return codes.length === 0 || codes.some((c: string) => c.startsWith(code) || c.startsWith(code.toUpperCase()));
}

export function invoiceDateStringError(invoiceDate: string): string {
  if (invoiceDate === "") {
    return "Invoice Date is required.";
  }
  if ((invoiceDate as any) instanceof Date) {
    return "";
  }
  return dateMustBeMMDDYYYY(invoiceDate);
}

export function postingDateStringError(postingDate: string): string {
  if (postingDate === "") {
    return "Posting Date is required.";
  }
  if ((postingDate as any) instanceof Date) {
    return "";
  }
  return dateMustBeMMDDYYYY(postingDate);
}

export function translationDateStringError(translationDate: string, postingDate: string): string {
  return translationDateNotInTheFuture(translationDate, postingDate);
}

export function referenceError(reference: string): string {
  const maxLengthMessage = requiredLength("reference", reference);
  if (maxLengthMessage !== "") {
    return maxLengthMessage;
  }

  if (!reference.match(/^[0-9a-z]+$/)) {
    return "Reference must be alphanumeric.";
  }

  return "";
}

export function descriptionError(description: string): string {
  return maxLength("description", description);
}

export function purposeError(purpose: string): string {
  return maxLength("purpose", purpose);
}

export function companyCodeError(companyCodes: string[], companyCode: string): string {
  if (companyCode === "") {
    return "Company Code is required.";
  }

  if (companyCode.length < 4) {
    return "Not a valid company code.";
  }
  const isValid = validateCodeInCodes(companyCodes, companyCode);
  if (!isValid) {
    return "Not a valid company code.";
  }
  return "";
}

export function currencyCodeError(currencyCodes: string[], currencyCode: string): string {
  if (currencyCode === "") {
    return "Currency Code is required.";
  }

  const isValid = validateCodeInCodes(currencyCodes, currencyCode);
  if (!isValid || currencyCode.length !== 3) {
    return "Not a valid currency code.";
  }
  return "";
}

export function documentTypeError(documentTypesCodes: string[], documentType: string): string {
  if (documentType === "") {
    return "Document Type is required.";
  }

  const isValid = validateCodeInCodes(documentTypesCodes, documentType);
  if (!isValid || documentType.length !== 2) {
    return "Not a valid document type.";
  }
  return "";
}

export function dateStringToDateObject(theDate: string): Date | undefined {
  // return a Date object instead
  return theDate !== "" && theDate !== null ? new Date(theDate) : undefined;
}

export function slEntryResponseToSlEntryGridItem(entry: ISLEntryResponse): ISLEntryGridItem {
  return {
    slEntryId: entry.slEntryId || 0,
    createdBy: "",
    nameDocumentId: entry.slDocNumber || leftPadDocumentNumber(entry.slEntryId),
    companyCode: entry.companyCode || "",
    description: "",
    status: "",
    poster: entry.additionalPoster || "",
    additionalPoster: entry.additionalPoster || "",
    fiscalYearAndPeriod: "",
    comments: "",
    modifiedDate: "",
    format: "",
    statusId: entry.jeStatus || 0,
    attachmentCount: 0,
    attachmentBlobName: "",
    attachmentFileName: "",
    reviewers: "",
    batchRefguid: entry.batchRefguid || "",
    refguid: entry.batchRefguid || "",
    isValidForAction: true,
    rowWeightage: 0
  };
}
